/**
 * Widget styles, scoped via shadow DOM. Calibrated to AMP Beauty's
 * DESIGN.md: warm neutrals + AMP purple accent + the AMP brand type stack
 * (Horizon for headers, Poppins for body). The widget is a different
 * surface than the B2B dashboard, but the brand DNA carries: warm white
 * over cold gray, AMP purple as the one expressive color, real typefaces
 * (no system-ui). Font files are self-hosted from the widget's backend
 * origin and loaded via injectBrandFonts() in main.tsx.
 *
 * Color system (matches dashboard DESIGN.md):
 *   --w-bg:        #FAFAF9   warm white
 *   --w-surface:   #FFFFFF
 *   --w-border:    #E8E4DF   warm gray
 *   --w-text:      #1C1917   warm near-black
 *   --w-muted:     #78716C
 *   --w-subtle:    #A8A29E
 *   --w-accent:    #6B3FA0   AMP purple
 *   --w-accent-lt: #F3EEFF   purple tint
 */

export const STYLES = `
:host {
  all: initial;
  font-family: "Poppins", system-ui, -apple-system, sans-serif;
  font-size: 15px;
  line-height: 1.5;
  color: #1C1917;
  --w-bg: #FAFAF9;
  --w-surface: #FFFFFF;
  --w-border: #E8E4DF;
  --w-border-md: #D6D0CA;
  --w-text: #1C1917;
  --w-muted: #78716C;
  --w-subtle: #A8A29E;
  --w-accent: #6B3FA0;
  --w-accent-hover: #57318A;
  --w-accent-lt: #F3EEFF;
  --w-accent-md: #DDD0F7;
}

* { box-sizing: border-box; }

button {
  font: inherit;
  color: inherit;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
}

input, textarea {
  font: inherit;
  color: inherit;
}

/* ----- Bubble ----- */
.bubble {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 56px;
  height: 56px;
  border-radius: 28px;
  background: var(--w-accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(107, 63, 160, 0.28);
  z-index: 2147483646;
  transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease;
}
.bubble:hover {
  transform: scale(1.04);
  background: var(--w-accent-hover);
  box-shadow: 0 6px 20px rgba(107, 63, 160, 0.36);
}
.bubble:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 3px;
}
.bubble svg { width: 24px; height: 24px; }

/* On mobile, the AMP storefront's sticky bottom nav already has an "AI
 * CHAT" tab linked to #ai-chat — which opens THIS widget. Showing the
 * floating bubble on top of that makes two chat entry points compete for
 * the same role, and shoppers can't tell which is the AI Beauty Chat.
 * Hide the floating bubble below the bottom-nav breakpoint (the bottom
 * nav itself hides at min-width: 960px in sections/bottom-menu.liquid;
 * we mirror that breakpoint here, inverted).
 *
 * The chat is still reachable on mobile via: the bottom-nav AI CHAT tab,
 * the AI Beauty drawer-menu item, the inline PDP block, and any
 * #ai-chat / ?chat=open link. The bubble itself is just a redundant
 * entry point on small screens. */
@media (max-width: 959px) {
  .bubble { display: none; }
}

/* ----- Modal ----- */
.modal {
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 380px;
  max-width: calc(100vw - 32px);
  height: 600px;
  max-height: calc(100vh - 48px);
  background: var(--w-surface);
  border-radius: 10px;
  border: 1px solid var(--w-border);
  box-shadow: 0 12px 40px rgba(28, 25, 23, 0.14);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2147483647;
  animation: slideUp 200ms ease-out;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 480px) {
  .modal {
    bottom: 0;
    right: 0;
    left: 0;
    top: 0;
    width: 100%;
    max-width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
    border: none;
  }
}

.header {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid var(--w-border);
  flex-shrink: 0;
}
.header-title {
  font-family: "Horizon", "Poppins", system-ui, sans-serif;
  font-weight: 600;
  font-size: 16px;
  letter-spacing: -0.01em;
  flex: 1;
  color: var(--w-text);
}
.header-subtitle {
  font-size: 12px;
  color: var(--w-muted);
  margin-top: 2px;
  font-weight: 400;
  letter-spacing: 0;
}
/* Close button.
 *
 * 44×44 minimum touch target (Apple HIG / Android Material). Used to be
 * 32×32 with muted gray — technically present but invisible enough that
 * shoppers couldn't find their way back to the storefront. Bumped to a
 * full touch target with a subtle background pill so it reads as a button,
 * and used --w-text (warm near-black) for high contrast on the surface. */
.close-btn {
  width: 44px;
  height: 44px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--w-text);
  background: var(--w-bg);
  transition: background 100ms ease, color 100ms ease, transform 80ms ease;
  flex-shrink: 0;
}
.close-btn svg { width: 20px; height: 20px; }
.close-btn:hover {
  background: var(--w-border);
  color: var(--w-text);
}
.close-btn:active { transform: scale(0.95); }
.close-btn:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 2px;
}

/* ----- Messages ----- */
.messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.message {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-width: 88%;
  white-space: pre-wrap;
  word-wrap: break-word;
}
.message.user {
  align-self: flex-end;
  background: var(--w-accent);
  color: #fff;
  padding: 10px 14px;
  border-radius: 14px 14px 4px 14px;
  font-weight: 450;
}
.message.assistant {
  align-self: flex-start;
}

/* Inline markdown rendered into the assistant text (renderMarkdown). */
.message-text .md-p {
  margin: 0 0 8px;
}
.message-text .md-p:last-child {
  margin-bottom: 0;
}
.message-text strong {
  font-weight: 600;
  color: var(--w-text);
}
.message-text em {
  font-style: italic;
}
.message-text .md-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.9em;
  padding: 1px 5px;
  background: var(--w-bg);
  border: 1px solid var(--w-border);
  border-radius: 4px;
}
.message-text .md-list {
  margin: 0 0 8px;
  padding-left: 20px;
}
.message-text .md-list:last-child {
  margin-bottom: 0;
}
.message-text .md-list li {
  margin: 0 0 2px;
}
.tool-notes {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 12px;
  color: var(--w-muted);
}
.tool-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
}
.tool-note::before {
  content: "";
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
  background: var(--w-accent-md);
  animation: pulse 1.4s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

.greeting {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 24px 8px;
  gap: 16px;
}
.greeting-text {
  color: var(--w-text);
  font-family: "Horizon", "Poppins", system-ui, sans-serif;
  font-size: 17px;
  font-weight: 500;
  line-height: 1.45;
  letter-spacing: -0.01em;
}
.greeting-sub {
  color: var(--w-muted);
  font-size: 13px;
  line-height: 1.5;
}
.suggested {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}
.suggested-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--w-subtle);
  margin-bottom: 2px;
}
.chip {
  text-align: left;
  padding: 10px 12px;
  border: 1px solid var(--w-border);
  border-radius: 10px;
  background: var(--w-surface);
  color: var(--w-text);
  font-size: 13.5px;
  font-weight: 450;
  transition: background 100ms ease, border-color 100ms ease;
}
.chip:hover {
  background: var(--w-accent-lt);
  border-color: var(--w-accent-md);
}
.chip:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 1px;
}

/* ----- Cards ----- */
.cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
.card {
  display: flex;
  gap: 10px;
  border: 1px solid var(--w-border);
  border-radius: 10px;
  padding: 8px;
  background: var(--w-surface);
  font-size: 13px;
  line-height: 1.4;
  text-decoration: none;
  color: inherit;
  transition: background 100ms ease, border-color 100ms ease;
}
.card:hover {
  background: var(--w-bg);
  border-color: var(--w-border-md);
}
.card-image {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  background: var(--w-bg);
  flex-shrink: 0;
  background-size: cover;
  background-position: center;
}
.card-body { flex: 1; min-width: 0; }
.card-title {
  font-weight: 500;
  color: var(--w-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.card-vendor {
  color: var(--w-muted);
  font-size: 12px;
  margin-top: 1px;
}
.card-price {
  color: var(--w-text);
  font-size: 13px;
  margin-top: 4px;
  font-weight: 500;
}

/* Add-to-bag button inside product recommendation cards.
 *
 * Sits below the price; small + restrained so it doesn't dominate the
 * card or hijack attention from the AI's prose response. AMP purple
 * idle state matches the bubble + send button — consistent CTA color
 * across the widget. Adding/Added/Error states give synchronous
 * feedback without needing a toast or modal.
 */
.card-add-btn {
  margin-top: 8px;
  padding: 7px 12px;
  border-radius: 9999px;
  background: var(--w-accent);
  color: #fff;
  font-family: "Poppins", system-ui, sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.1px;
  border: none;
  cursor: pointer;
  align-self: flex-start;
  transition: background 100ms ease, transform 80ms ease, opacity 100ms ease;
}
.card-add-btn:hover:not(:disabled) { background: var(--w-accent-hover); }
.card-add-btn:active:not(:disabled) { transform: scale(0.97); }
.card-add-btn:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 2px;
}
.card-add-btn--adding {
  background: var(--w-accent);
  opacity: 0.72;
  cursor: progress;
}
.card-add-btn--added {
  background: #059669; /* same green as the dashboard's invoiced status */
  cursor: default;
}
.card-add-btn--added:hover { background: #059669; }
.card-add-btn--error {
  background: var(--w-surface);
  color: #B91C1C;
  border: 1px solid #FECACA;
  font-weight: 500;
}
.card-add-btn--error:hover { background: #FEF2F2; }

/* ----- Composer ----- */
.composer {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--w-border);
  flex-shrink: 0;
  background: var(--w-surface);
}

/* AI disclosure note shown under the composer. Small, muted text so it's
 * present for trust + compliance but never competes with the input. Links
 * use the underline-on-hover pattern (not always underlined) to stay
 * quiet visually inside an already-compact UI. */
.disclaimer {
  padding: 0 16px 12px;
  font-size: 10px;
  line-height: 1.45;
  color: var(--w-muted);
  background: var(--w-surface);
  flex-shrink: 0;
}
.disclaimer a {
  color: var(--w-text);
  text-decoration: none;
  border-bottom: 1px solid var(--w-border-md);
}
.disclaimer a:hover {
  color: var(--w-accent);
  border-bottom-color: var(--w-accent);
}
.disclaimer a:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 2px;
  border-radius: 2px;
}
.composer textarea {
  flex: 1;
  min-height: 36px;
  max-height: 120px;
  padding: 8px 12px;
  border: 1px solid var(--w-border);
  border-radius: 18px;
  resize: none;
  outline: none;
  background: var(--w-bg);
  font-size: 14px;
  line-height: 1.4;
  color: var(--w-text);
  transition: border-color 100ms ease, background 100ms ease;
}
.composer textarea::placeholder { color: var(--w-subtle); }
.composer textarea:focus {
  border-color: var(--w-accent);
  background: var(--w-surface);
}
.composer textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.send-btn {
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background: var(--w-accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: flex-end;
  transition: background 100ms ease;
}
.send-btn:not(:disabled):hover { background: var(--w-accent-hover); }
.send-btn:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 2px;
}
.send-btn:disabled {
  background: var(--w-border-md);
  cursor: not-allowed;
}
.send-btn svg { width: 16px; height: 16px; }

.error-banner {
  background: #FEF2F2;
  color: #991B1B;
  padding: 8px 16px;
  font-size: 13px;
  border-top: 1px solid #FECACA;
  flex-shrink: 0;
}

/* ============================================================
 * Inline PDP block (separate shadow root, same design tokens)
 * ============================================================ */

:host {
  /* Token re-declaration intentional — when this stylesheet is shared
     with the inline shadow root, the variables apply there too. */
}

.pdp-inline {
  background: linear-gradient(135deg, #F3EEFF 0%, #FBE9F4 45%, #FFF1E6 100%);
  border-radius: 16px;
  padding: 22px 24px 20px;
  font-family: "Poppins", system-ui, -apple-system, sans-serif;
  color: var(--w-text);
}

.pdp-inline-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.pdp-sparkle {
  display: inline-flex;
  width: 22px;
  height: 22px;
  color: var(--w-accent);
  flex-shrink: 0;
}
.pdp-sparkle svg { width: 100%; height: 100%; }

.pdp-title {
  font-family: "Horizon", "Poppins", system-ui, sans-serif;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--w-text);
  margin: 0;
  flex-shrink: 0;
}

.pdp-new-badge {
  font-family: "Poppins", system-ui, sans-serif;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  background: var(--w-text);
  color: #fff;
  padding: 3px 8px;
  border-radius: 9999px;
  line-height: 1.2;
}

.pdp-input-row {
  display: flex;
  align-items: center;
  background: var(--w-surface);
  border-radius: 9999px;
  padding: 4px 4px 4px 18px;
  margin-bottom: 14px;
  border: 1px solid transparent;
  transition: border-color 100ms ease, box-shadow 100ms ease;
}
.pdp-input-row:focus-within {
  border-color: var(--w-accent-md);
  box-shadow: 0 0 0 3px rgba(107, 63, 160, 0.12);
}

.pdp-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14.5px;
  padding: 12px 0;
  color: var(--w-text);
  min-width: 0;
}
.pdp-input::placeholder { color: var(--w-muted); }

.pdp-send {
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  background: var(--w-text);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 100ms ease, transform 80ms ease;
}
.pdp-send svg { width: 16px; height: 16px; }
.pdp-send:not(:disabled):hover {
  background: var(--w-accent);
}
.pdp-send:not(:disabled):active { transform: scale(0.96); }
.pdp-send:disabled {
  background: var(--w-subtle);
  cursor: not-allowed;
}
.pdp-send:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 2px;
}

.pdp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.pdp-chip {
  background: var(--w-surface);
  border: 1px solid transparent;
  border-radius: 9999px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 450;
  color: var(--w-text);
  transition: background 100ms ease, border-color 100ms ease, transform 80ms ease;
  white-space: nowrap;
}
.pdp-chip:hover {
  border-color: var(--w-accent-md);
  background: var(--w-surface);
}
.pdp-chip:active { transform: translateY(1px); }
.pdp-chip:focus-visible {
  outline: 2px solid var(--w-accent);
  outline-offset: 1px;
}

/* Mobile optimization.
 *
 * Two real fixes here, not cosmetic:
 *   1. Touch targets — Apple HIG mandates ≥44×44 and Android Material ≥48×48
 *      for tappable elements. Desktop styling had .pdp-send at 36×36 and
 *      .pdp-chip rendering at ~38px tall. Both are too small for confident
 *      mobile taps; mis-taps directly hurt engagement-rate which is the
 *      whole metric this widget exists to move.
 *   2. Vertical density — when the inline block sits in the buy-box column,
 *      mobile shoppers scroll past it during the buy decision. Tightening
 *      padding + horizontally scrolling the chips (instead of wrapping to
 *      3 rows) keeps the block visible without dominating the viewport.
 *
 * 16px font-size on inputs prevents iOS Safari auto-zoom on focus.
 */
@media (max-width: 480px) {
  .pdp-inline {
    padding: 16px 14px 14px;
    border-radius: 12px;
  }
  .pdp-title { font-size: 17px; }

  .pdp-input-row { padding: 4px 4px 4px 14px; }
  .pdp-input {
    font-size: 16px;        /* ≥16px prevents iOS Safari focus zoom */
    padding: 12px 0;
    min-height: 44px;
  }

  .pdp-send {
    width: 44px;
    height: 44px;
  }
  .pdp-send svg { width: 18px; height: 18px; }

  /* Chips: horizontal-scroll strip instead of wrap. Single row preserves
   * vertical space and reads as a swipable carousel — familiar pattern. */
  .pdp-chips {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;        /* Firefox */
    margin: 0 -14px;              /* full-bleed inside .pdp-inline padding */
    padding: 0 14px 4px;
    scroll-padding-left: 14px;
    scroll-snap-type: x proximity;
  }
  .pdp-chips::-webkit-scrollbar { display: none; }
  .pdp-chip {
    flex-shrink: 0;
    min-height: 44px;
    padding: 10px 16px;
    font-size: 13px;
    scroll-snap-align: start;
  }
}
`;
